/**
 * Module 4 — DCF panel (+ Module 5 Monte Carlo). Runs the Python DCF engine: UFCF defaults to
 * QofE Defensible EBITDA. Shows EV/equity, dual terminal values, LBO↔DCF cross-check, flags,
 * and a seeded Monte Carlo on IRR (reuses the LBO base).
 */

import React, { useState } from 'react';
import { dcfApi, sensitivityApi } from '../services/api';

const ACCENT = '#161f4a';
const money = (s) => (s == null ? '—' : Number(s).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));
const pctv = (s) => (s == null ? '—' : `${(Number(s) * 100).toFixed(1)}%`);

export default function DCFPanel({ dealId }) {
  const [inputs, setInputs] = useState({ ufcf_year1: '', ufcf_growth: 0.05, proj_years: 5, wacc: 0.10, terminal_growth: 0.03, exit_multiple: 6, terminal_ebitda: '', net_debt: 0 });
  const [model, setModel] = useState(null);
  const [cross, setCross] = useState(null);
  const [mc, setMc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setInputs({ ...inputs, [k]: v === '' ? '' : Number(v) });

  const run = async () => {
    setBusy(true); setError(null);
    try {
      const payload = { ...inputs };
      // drop empties so backend can auto-fill from QofE if user leaves it blank
      if (payload.ufcf_year1 === '') delete payload.ufcf_year1;
      if (payload.terminal_ebitda === '') delete payload.terminal_ebitda;
      const res = await dcfApi.run(dealId, { inputs: payload });
      setModel(res.data.model); setCross(res.data.cross_check);
    } catch (e) { setError(e?.response?.data?.error || e?.response?.data?.message || 'DCF run failed (service running?).'); }
    finally { setBusy(false); }
  };

  const runMc = async () => {
    setBusy(true); setError(null);
    try {
      const res = await sensitivityApi.run(dealId, {
        run_type: 'monte_carlo', metric: 'irr', seed: 42, trials: 5000,
        distributions: {
          ebitda_growth: { dist: 'triangular', low: 0, mode: 0.05, high: 0.1 },
          exit_multiple: { dist: 'triangular', low: 5, mode: 6, high: 7 },
        },
      });
      setMc(res.data.run.results);
    } catch (e) { setError(e?.response?.data?.message || 'Monte Carlo failed (run an LBO first).'); }
    finally { setBusy(false); }
  };

  const r = model && model.results;
  const FIELDS = [
    ['ufcf_year1', 'UFCF Year 1 ($)'],
    ['ufcf_growth', 'UFCF growth'],
    ['proj_years', 'Years'],
    ['wacc', 'WACC'],
    ['terminal_growth', 'Terminal g'],
    ['exit_multiple', 'Exit × (opt)'],
    ['terminal_ebitda', 'Term. EBITDA (opt)'],
    ['net_debt', 'Net debt ($)'],
  ];

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        {FIELDS.map(([k, label]) => (
          <label key={k} style={{ fontSize: 12, color: '#64748b', display: 'flex', flexDirection: 'column' }}>
            {label}
            <input type="number" step="any" value={inputs[k] ?? ''} onChange={e => set(k, e.target.value)}
              style={{ width: 120, padding: '4px 6px', border: '1px solid #cbd5e1', borderRadius: 6 }} />
          </label>
        ))}
        <button onClick={run} disabled={busy} style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>
          {busy ? 'Running…' : 'Run DCF'}
        </button>
        <button onClick={runMc} disabled={busy} style={{ background: '#fff', color: ACCENT, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: '8px 16px', cursor: 'pointer' }}>
          Monte Carlo (IRR)
        </button>
      </div>

      {error && <div style={{ color: '#b91c1c', marginTop: 10 }}>{error}</div>}

      {r && (
        <div style={{ marginTop: 16, display: 'flex', gap: 28, flexWrap: 'wrap' }}>
          <Stat label="Enterprise value" value={money(r.enterprise_value)} />
          <Stat label="Equity value" value={money(r.equity_value)} />
          <Stat label="WACC" value={pctv(r.wacc)} />
          <Stat label="TV (Gordon)" value={money(r.tv_gordon)} />
          {r.tv_exit != null && <Stat label="TV (exit ×)" value={money(r.tv_exit)} />}
          {cross && <Stat label="LBO↔DCF var" value={pctv(cross.variance)} warn={cross.flag} />}
        </div>
      )}

      {r && r.flags && r.flags.length > 0 && (
        <ul style={{ marginTop: 12, paddingLeft: 18, fontSize: 13 }}>
          {r.flags.map((f, i) => <li key={i} style={{ color: '#b45309' }}><strong>{f.title}</strong> — {f.detail}</li>)}
        </ul>
      )}
      {cross && cross.flag && <div style={{ marginTop: 8, color: '#b45309', fontSize: 13 }}>⚠ LBO and DCF enterprise values diverge &gt; 15%.</div>}

      {mc && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '0 0 6px' }}>Monte Carlo — IRR ({mc.valid}/{mc.trials} trials, seed {mc.seed})</h4>
          <div style={{ display: 'flex', gap: 24 }}>
            <Stat label="P10" value={pctv(mc.p10)} />
            <Stat label="P50" value={pctv(mc.p50)} />
            <Stat label="P90" value={pctv(mc.p90)} />
            <Stat label="Prob. loss" value={pctv(mc.prob_loss)} warn={mc.prob_loss > 0.1} />
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, warn }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: warn ? '#b45309' : ACCENT }}>{value}</div>
    </div>
  );
}
